import { BillingBanner } from './BillingBanner';
import { useState, type ComponentType, type FormEvent } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  Bot,
  Building2,
  LayoutDashboard,
  GitBranch,
  Inbox,
  Menu,
  MessagesSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Settings,
  Users,
  X,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useTicketCounts } from '@/api/tickets';
import type { TicketCounts } from '@/api/types';
import type { AbilityCheck } from '../auth/ability';
import { Avatar } from '@/components/ui';
import { humanize } from '@/lib/format';
import { useAuth } from '../auth/useAuth';
import { UserMenu } from './UserMenu';
import { StatusSwitcher } from './StatusSwitcher';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** CASL check, e.g. `['read', 'Ticket']`; hidden when the role can't. */
  can?: AbilityCheck;
  /** Match the path exactly (an index route like /admin shouldn't light up under /admin/clients). */
  end?: boolean;
  /** Live count shown beside the label. */
  count?: keyof TicketCounts;
}

/** Platform console — only super admins pass `manage Workspace`. */
const PLATFORM_NAV: { heading: string; items: NavItem[] } = {
  heading: 'Platform',
  items: [
    { to: '/admin', label: 'Overview', icon: LayoutDashboard, can: ['manage', 'Workspace'], end: true },
    { to: '/admin/clients', label: 'Clients', icon: Building2, can: ['manage', 'Workspace'] },
  ],
};

/** Inside a client workspace. */
const NAV: { heading: string; items: NavItem[] }[] = [
  {
    heading: 'Support',
    items: [
      { to: '/inbox', label: 'Inbox', icon: Inbox, can: ['read', 'Ticket'], count: 'mine' },
      {
        to: '/chats',
        label: 'Live chats',
        icon: MessagesSquare,
        can: ['read', 'Ticket'],
        count: 'liveChats',
      },
      { to: '/customers', label: 'Customers', icon: Users, can: ['read', 'Customer'] },
    ],
  },
  {
    heading: 'Knowledge',
    items: [{ to: '/kb', label: 'Knowledge base', icon: BookOpen, can: ['read', 'KnowledgeBase'] }],
  },
  {
    heading: 'Insights',
    items: [{ to: '/analytics', label: 'Analytics', icon: BarChart3, can: ['read', 'Analytics'] }],
  },
  {
    heading: 'Automation',
    items: [
      { to: '/workflows', label: 'Workflow engine', icon: GitBranch, can: ['manage', 'Workflow'] },
      { to: '/ai-agents', label: 'AI agents', icon: Bot, can: ['manage', 'AiSettings'] },
    ],
  },
];

export function Logo({ compact = false, dark = false }: { compact?: boolean; dark?: boolean }) {
  return (
    <span className="flex items-center gap-2.5">
      <img src="/favicon.svg" alt="" className="h-8 w-8 drop-shadow-[0_4px_12px_rgba(139,92,246,0.45)]" />
      {!compact && (
        <span className="leading-tight">
          <span
            className={cn('font-heading block text-[15px] font-bold', dark ? 'text-white' : 'text-slate-900')}
          >
            Kollabr
          </span>
          <span
            className={cn(
              'block text-[11px] font-medium tracking-wide',
              dark ? 'text-ink-400' : 'text-slate-500',
            )}
          >
            Support desk
          </span>
        </span>
      )}
    </span>
  );
}

function NavCount({
  count,
  active,
  collapsed,
}: {
  count: keyof TicketCounts;
  active: boolean;
  collapsed?: boolean;
}) {
  const counts = useTicketCounts();
  const n = counts.data?.[count];
  if (!n) return null;
  if (collapsed) {
    return (
      <span className="bg-brand-500 ring-ink-950 absolute top-0.5 right-1 min-w-4 rounded-full px-1 text-center text-[9px] leading-4 font-bold text-white tabular-nums ring-2">
        {n > 99 ? '99+' : n}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'ml-auto min-w-5 rounded-full px-1.5 text-center text-[10.5px] leading-[18px] font-semibold tabular-nums',
        active ? 'bg-white/20 text-white' : 'bg-ink-700 text-ink-300',
      )}
    >
      {n}
    </span>
  );
}

function NavItemLink({
  item,
  onNavigate,
  collapsed = false,
}: {
  item: NavItem;
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const { to, label, icon: Icon, count } = item;
  return (
    <NavLink
      to={to}
      end={item.end}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center rounded-lg text-[13px] font-medium transition-all',
          collapsed ? 'mx-auto h-10 w-10 justify-center' : 'gap-2.5 px-2.5 py-[7px]',
          isActive
            ? 'from-brand-600/90 to-brand-500/70 shadow-brand-900/40 bg-gradient-to-r text-white shadow-md'
            : 'text-ink-300 hover:bg-ink-800 hover:text-white',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={cn(
              'h-[17px] w-[17px] shrink-0',
              isActive ? 'text-white' : 'text-ink-400 group-hover:text-white',
            )}
          />
          <span className={cn(collapsed ? 'sr-only' : 'truncate')}>{label}</span>
          {count && <NavCount count={count} active={isActive} collapsed={collapsed} />}
        </>
      )}
    </NavLink>
  );
}

function WorkspaceTag({ collapsed = false }: { collapsed?: boolean }) {
  const { workspace, isPlatformAdmin } = useAuth();
  if (!workspace && !isPlatformAdmin) return null;
  const name = workspace?.name ?? 'Kollabr admin';
  if (collapsed) {
    return (
      <span
        title={`${workspace ? 'Workspace' : 'Platform'}: ${name}`}
        className="border-ink-700/70 bg-ink-800/80 mx-auto mt-1 flex h-9 w-9 items-center justify-center rounded-lg border text-sm font-bold text-white uppercase"
      >
        {name.charAt(0)}
      </span>
    );
  }
  return (
    <div className="border-ink-700/70 bg-ink-800/60 mx-3 mt-1 rounded-lg border px-2.5 py-1.5">
      <p className="text-ink-400 text-[9.5px] font-semibold tracking-[0.14em] uppercase">
        {workspace ? 'Workspace' : 'Platform'}
      </p>
      <p className="truncate text-[13px] font-semibold text-white">{name}</p>
    </div>
  );
}

function Sidebar({
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
  /** Desktop only: shrink to an icon rail / expand again. */
  onToggleCollapsed?: () => void;
}) {
  const { can, user, workspace } = useAuth();
  // Tenant pages only exist inside a workspace; a super admin outside one sees the console only.
  const sections = [PLATFORM_NAV, ...(workspace ? NAV : [])];
  return (
    <>
      <WorkspaceTag collapsed={collapsed} />
      <nav
        aria-label="Main"
        className={cn('flex flex-1 flex-col overflow-y-auto py-3', collapsed ? 'gap-2 px-2' : 'gap-4 px-3')}
      >
        {sections.map((section, i) => {
          const items = section.items.filter((n) => !n.can || can(...n.can));
          if (items.length === 0) return null;
          return (
            <div key={section.heading}>
              {collapsed ? (
                i > 0 && <div className="bg-ink-700/70 mx-auto mb-2 h-px w-6" aria-hidden />
              ) : (
                <p className="text-ink-400/80 mb-1 px-2.5 text-[9.5px] font-semibold tracking-[0.14em] uppercase">
                  {section.heading}
                </p>
              )}
              <div className="flex flex-col gap-0.5">
                {items.map((item) => (
                  <NavItemLink key={item.to} item={item} onNavigate={onNavigate} collapsed={collapsed} />
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <div className={cn('space-y-2', collapsed ? 'px-2 py-3' : 'p-3')}>
        {workspace && (
          <NavItemLink
            item={{ to: '/settings', label: 'Settings', icon: Settings }}
            onNavigate={onNavigate}
            collapsed={collapsed}
          />
        )}
        {onToggleCollapsed && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn(
              'text-ink-400 hover:bg-ink-800 flex items-center rounded-lg text-[13px] font-medium transition-colors hover:text-white',
              collapsed ? 'mx-auto h-10 w-10 justify-center' : 'w-full gap-2.5 px-2.5 py-[7px]',
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-[17px] w-[17px]" />
            ) : (
              <PanelLeftClose className="h-[17px] w-[17px]" />
            )}
            {!collapsed && 'Collapse'}
          </button>
        )}
        {collapsed ? (
          <span className="flex justify-center pt-1" title={`${user.name} · ${humanize(user.role)}`}>
            <Avatar name={user.name} src={user.avatarUrl} status={user.status} size="sm" />
          </span>
        ) : (
          <div className="border-ink-700/70 bg-ink-800/60 flex items-center gap-2.5 rounded-xl border p-2">
            <Avatar name={user.name} src={user.avatarUrl} status={user.status} size="sm" />
            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold text-white">{user.name}</p>
              <p className="text-ink-400 truncate text-[11px]">{humanize(user.role)}</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/** Shown while a super admin works inside a client's workspace. */
function PlatformBanner() {
  const { isPlatformAdmin, workspace, leaveWorkspace } = useAuth();
  if (!isPlatformAdmin || !workspace) return null;
  return (
    <div className="bg-accent-500 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-1.5 text-center text-xs font-medium text-white">
      <span>
        You’re inside <strong>{workspace.name}</strong> as platform admin — changes affect this client.
      </span>
      <button
        type="button"
        onClick={() => leaveWorkspace()}
        className="inline-flex items-center gap-1 rounded-md bg-white/15 px-2 py-0.5 hover:bg-white/25"
      >
        <ArrowLeft className="h-3 w-3" /> Back to clients
      </button>
    </div>
  );
}

function QuickSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    navigate(`/inbox?view=all&q=${encodeURIComponent(term)}`);
    setQ('');
  };
  return (
    <form onSubmit={submit} role="search" className="relative hidden w-full max-w-md md:block">
      <Search className="pointer-events-none absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <label htmlFor="quick-search" className="sr-only">
        Search tickets
      </label>
      <input
        id="quick-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search tickets by subject or #number…"
        className="focus:border-brand-300 focus:ring-brand-500/10 h-10 w-full rounded-xl border border-slate-200/80 bg-slate-50/80 pr-3 pl-10 text-sm placeholder:text-slate-400 focus:bg-white focus:ring-4 focus:outline-none"
      />
    </form>
  );
}

const COLLAPSED_KEY = 'ks:sidebar-collapsed';
const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

export function AppShell() {
  const { workspace } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const location = useLocation();
  const toggleCollapsed = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, c ? '0' : '1');
      } catch {
        // Storage blocked: the choice lasts for this visit only.
      }
      return !c;
    });

  return (
    <div className="flex min-h-dvh">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-white focus:px-3 focus:py-2 focus:shadow"
      >
        Skip to content
      </a>

      {/* Desktop sidebar */}
      <aside
        className={cn(
          'bg-ink-950 sticky top-0 hidden h-dvh shrink-0 flex-col transition-[width] duration-200 lg:flex',
          collapsed ? 'w-[72px]' : 'w-56',
        )}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-[radial-gradient(60%_80%_at_20%_0%,rgba(139,92,246,0.28),transparent)]" />
        <div className={cn('relative flex h-16 items-center', collapsed ? 'justify-center' : 'px-4')}>
          <Logo dark compact={collapsed} />
        </div>
        <div className="relative flex min-h-0 flex-1 flex-col">
          <Sidebar collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="bg-ink-950 shadow-pop relative flex h-full w-64 flex-col">
            <div className="flex h-16 items-center justify-between px-5">
              <Logo dark />
              <button
                type="button"
                className="text-ink-300 hover:bg-ink-800 rounded-lg p-1.5"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <PlatformBanner />
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200/70 bg-white/80 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
          <button
            type="button"
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="lg:hidden">
            <Logo compact />
          </span>
          {workspace && <QuickSearch />}
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            {workspace && <StatusSwitcher />}
            <UserMenu />
          </div>
        </header>
        <BillingBanner />
        <main
          id="main"
          key={location.pathname.split('/')[1]}
          className="animate-fade-in flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
